import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HelloUser } from '@/components/ui';
import { CategoryTabsContainer } from '@/components/features/CategoryTabs/CategoryTabsContainer';
import { TopBarContainer } from '@/components/features/TopBar/TopBarContainer';
import { AppLogo } from '@/components/icons/AppLogo';
import { cn } from '@/lib/utils';
import { useAppUser } from '@/lib/useAppUser';
import { useAppAuth } from '@/lib/useAppAuth';
import { setHeaderStuck } from '@/store';

/**
 * Routes that render a slimmed-down top bar — no time-of-day greeting.
 * The sticky balance row still applies; only the scroll-away greeting
 * block is omitted. (Category navigation is Feed-only, gated separately.)
 */
const SLIM_ROUTES = ['/portfolio', '/wallet', '/events'];

const TEST_ID_PREFIX = 'home-top-bar';

/**
 * App-wide top section rendered once by the app shell so it survives
 * route transitions. Bundles the brand mark, time-of-day greeting,
 * balance + crown bar (`TopBarContainer`), and category navigation.
 *
 * Layout is split in two on every route:
 *  - a scroll-away block holding the logo (and on home, the greeting),
 *    which slides out of view as the user scrolls;
 *  - a `sticky top-0` block holding the balance row (and on home, the
 *    category tabs), which pins to the top once the scroll-away block
 *    is past. While pinned, the inner components render in compact
 *    mode and the bar gains a translucent white backdrop.
 *
 * `TopBarContainer` is intentionally instantiated here only — pages
 * must not render their own copy.
 */
export function HomeTopBar() {
  const { pathname } = useLocation();
  const user = useAppUser();
  const { isAuthenticated } = useAppAuth();

  const isSlim = SLIM_ROUTES.some((route) => pathname.startsWith(route));
  // The category navigation belongs to the Feed page only. Every other route
  // (live, loyalty, wallet, search, …) renders just the greeting + balance.
  const isFeed = pathname === '/';

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  // Publish the pinned state so pages stacking their own sticky element below
  // the top bar (e.g. the wallet tabs) can mirror its pinned styling.
  useEffect(() => {
    setHeaderStuck(isStuck);
  }, [isStuck]);

  // The sentinel sits at the start of the sticky bar's natural position.
  // We compare scrollY to the sentinel's document offset with an 8px
  // hysteresis band — without it, sub-pixel jitter and the compact-mode
  // height transition cause `isStuck` to oscillate at the boundary.
  useEffect(() => {
    const STICK_HYSTERESIS_PX = 8;
    let scheduled = false;

    const evaluate = () => {
      scheduled = false;
      const sentinel = sentinelRef.current;
      if (!sentinel) return;
      const triggerY = sentinel.getBoundingClientRect().top + window.scrollY;
      const y = window.scrollY;
      setIsStuck((prev) => {
        if (!prev && y > triggerY + STICK_HYSTERESIS_PX) return true;
        if (prev && y < triggerY - STICK_HYSTERESIS_PX) return false;
        return prev;
      });
    };

    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <>
      {/* --status-bar-height is a CSS custom property injected by the RN WebView's
          injectedJavaScript in AppWebView.tsx, set to the device's safe-area top inset. */}
      <div
        className={cn(
          'px-4 md:px-6 lg:px-8 xl:px-10',
          !user ? 'pt-[var(--status-bar-height,0px)] md:pt-0' : 'pt-[calc(var(--status-bar-height,0px)+1.25rem)] md:pt-6',
        )}
      >
        {/* At md+ the SideNav owns the logo and identity block, so both hide there. */}
        {isAuthenticated && (
          <Link to="/" aria-label="Go to home" className="inline-block w-fit md:hidden">
            <AppLogo disableAnimation={true} />
          </Link>
        )}
        {!isSlim && user?.displayName && isAuthenticated && (
          <HelloUser data-testid={`${TEST_ID_PREFIX}-greeting`} className="md:hidden" userName={user?.displayName} />
        )}
      </div>
      <div ref={sentinelRef} aria-hidden className="h-px" />
      <div
        data-testid={TEST_ID_PREFIX}
        className={cn(
          // `md:top-4` + `pt-2` pin the desktop card 24px from the viewport top,
          // matching the SideNav's inset (sticky `top` only affects the pinned
          // position, so the resting layout is unchanged).
          'sticky top-0 md:top-4 z-30 px-4 pb-2 md:px-6 lg:px-8 xl:px-10 transition-[colors,padding] duration-300 ease-in-out',
          // Pinned on mobile: full-bleed translucent band. On authenticated
          // desktop the band is transparent — the inner wrapper below becomes a
          // floating card instead, so content scrolls visibly around it.
          isStuck
            ? cn(
                'pt-[calc(var(--status-bar-height,0px)+0.5rem)] md:pt-2 bg-surface/70 backdrop-blur-md',
                isAuthenticated && 'md:bg-transparent md:backdrop-blur-none',
              )
            : 'pt-2 bg-transparent',
        )}
      >
        <div
          className={cn(
            'transition-[background-color,padding] duration-150',
            // Pinned desktop treatment (per design): the category bar floats as
            // a rounded card with the same chrome as the SideNav card.
            isStuck &&
              isAuthenticated &&
              isFeed &&
              'md:rounded-xl md:border-[1.5px] md:border-input-soft md:bg-surface/70 md:backdrop-blur-md md:p-3',
          )}
        >
          {/* The SideNav owns avatar/balance/notifications at md+ — hide the
              authenticated balance row there (guests keep GuestTopBar at all widths). */}
          <div className={cn(isAuthenticated && 'md:hidden')}>
            <TopBarContainer compact={isStuck} />
          </div>
          {/* No md:overflow-hidden on the tab row — the desktop search popover
              anchors inside it and must escape it (the tabs clip themselves). */}
          {isFeed && isAuthenticated && (
            <CategoryTabsContainer className="md:border-none md:rounded-card" compact={isStuck} />
          )}
        </div>
      </div>
    </>
  );
}
